import { inEnum, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { StaticContentSizing } from "../../../lib/dynamicscaler/ContentSizing.js";
import { parseSizeSpecification, VideoContainerType, VideoFormatRawData } from "../../../model/video/VideoFormat.js";
import { VideoInfoRawData } from "../../../model/video/VideoInfo.js";


/**
 * Options for specifying video size preferences.
 *
 * @property size - The target size value for the video (e.g., pixel width or height).
 * @property dimension - The dimension to consider when applying the size preference.
 *   - "width": Match based on video width.
 *   - "height": Match based on video height.
 *   - "significant": Match based on the most significant dimension.
 * @property fallback - Strategy to use if an exact match for the size is not found.
 *   - "lowest": Select the lowest available size.
 *   - "lower": Select the next lower available size.
 *   - "higher": Select the next higher available size.
 *   - "highest": Select the highest available size.
 */
export type SizePreferenceOptions = {

    size: number,
    dimension: "width" | "height" | "significant" ,
    fallback: "lowest" | "lower" | "higher" | "highest"
    
}

/**
 * Represents generic video format labels based on resolution.
 * 
 * - `p240`: 240p resolution.
 * - `p360`: 360p resolution.
 * - `p480`: 480p resolution.
 * - `p720`: 720p resolution (HD).
 * - `p1080`: 1080p resolution (Full HD).
 * - `p2160`: 2160p resolution (4K UHD).
 * - `UNSPECIFIED`: Unspecified or unknown resolution.
 */
export enum GenericFormatLabel {

    p240 = "240p",
    p360 = "360p",
    p480 = "480p",
    p720 = "720p",
    p1080 = "1080p",
    p2160 = "2160p",
    UNSPECIFIED = "-"

}



export function isGenericFormatLabel(a: any): a is GenericFormatLabel {
    return isString(a) && inEnum(a,GenericFormatLabel);
}


/**
 * Represents a video format with its associated size, URL, original definition, label, and main size.
 *
 * @property parsedSize - The static sizing information for the video content.
 * @property url - The URL pointing to the video resource.
 * @property originalDef - The raw data definition of the video format.
 * @property label - A generic label describing the format.
 * @property mainSize - The primary size value for the format, typically in bytes or pixels.
 */
export type ParsedFormat = {
    parsedSize: StaticContentSizing,
    url: string,
    originalDef: VideoFormatRawData,
    label: GenericFormatLabel,
    mainSize: number
}


/**
 * Defines a policy for selecting a preferred video format based on size constraints.
 *
 * Implementations of this interface allow picking a format from a list of parsed formats,
 * updating the target size, and retrieving the current target size.
 *
 * @remarks
 * This is typically used to optimize video playback for bandwidth or device limitations.
 *
 * @method pickFromFormats Selects the most suitable format from the provided list according to the policy.
 * @param fmt Array of available parsed formats.
 * @returns The selected format, or `null` if none match the policy.
 *
 * @method updateTargetSize Updates the desired target size for format selection.
 * @param size The new target size in bytes.
 *
 * @method getTargetSize Retrieves the current target size used for format selection.
 * @returns The target size in bytes.
 */
export interface SizePreferencePolicy {

    pickFromFormats(fmt: ParsedFormat[]): ParsedFormat | null;
    updateTargetSize(size: number): void;
    getTargetSize(): number;

}


/**
 * Returns a generic format label based on the minimum dimension (height or width) of the provided static content size.
 *
 * The function maps the minimum of `height` and `width` to a corresponding `GenericFormatLabel`:
 * - `<= 300`: `p240`
 * - `<= 420`: `p360`
 * - `<= 600`: `p480`
 * - `<= 900`: `p720`
 * - `<= 1620`: `p1080`
 * - otherwise: `p2160`
 *
 * @param pSize - The static content sizing object containing `height` and `width` properties.
 * @returns The corresponding `GenericFormatLabel` based on the minimum dimension.
 */
function formatLabeler(pSize: StaticContentSizing): GenericFormatLabel {
    const s = Math.min(pSize.height,pSize.width);
    if(s <= 300) return GenericFormatLabel.p240;
    else if ( s <= 420) return GenericFormatLabel.p360;
    else if ( s <= 600) return GenericFormatLabel.p480;
    else if ( s <= 900) return GenericFormatLabel.p720;
    else if ( s <= 1620) return GenericFormatLabel.p1080;
    else return GenericFormatLabel.p2160;
}

/**
 * Determines the preferred video size based on the provided static content dimensions.
 *
 * The function selects a standard video height (e.g., 240, 360, 480, 720, 1080, or 2160)
 * according to the smaller value between the given height and width.
 *
 * @param pSize - An object representing the static content's dimensions, containing `height` and `width` properties.
 * @returns The preferred video size as a number, corresponding to a standard video height.
 */
function mainSize(pSize: StaticContentSizing): number {
    const s = Math.min(pSize.height,pSize.width);
    if(s <= 300) return 240;
    else if ( s <= 420) return 360;
    else if ( s <= 600) return 480;
    else if ( s <= 900) return 720;
    else if ( s <= 1620) return 1080;
    else return 2160;
}

/**
 * Filters and parses video formats from the provided raw video information, returning an array of parsed formats
 * for the specified container type. Each parsed format includes its size, URL, original definition, label, and main size.
 * The resulting array is sorted in descending order by the area of the parsed size.
 *
 * @param container - The video container type to filter formats by.
 * @param vinfo - The raw video information containing available formats.
 * @returns An array of parsed formats matching the specified container, sorted by size area.
 */
export function getParsedFormats(container: VideoContainerType, vinfo: VideoInfoRawData): ParsedFormat[] {
    return vinfo.format.filter(e => e.container == container).map(x => {
        const psize = parseSizeSpecification(x.size);    
        const usize = psize && psize instanceof StaticContentSizing?psize:null;
        return {
            parsedSize: usize,
            url: x.resourceUrl,
            originalDef: x,
            label: usize?formatLabeler(usize):GenericFormatLabel.UNSPECIFIED,
            mainSize: usize?mainSize(usize): null,
        }
    }).sort((a,b) => {
        return b.parsedSize.getArea() - a.parsedSize.getArea()
     } );

}


/**
 * Implements the {@link SizePreferencePolicy} interface to select the most suitable video format
 * based on user-defined size preferences (width, height, or minimum dimension).
 *
 * The policy sorts available formats by area and calculates their distance from the target size.
 * It then selects the format that best matches the preference, using a fallback strategy if no exact match is found.
 *
 * @remarks
 * - The selection logic considers the preferred dimension (`width`, `height`, or `min`).
 * - Fallback strategies include choosing the lowest, highest, or closest lower/higher size.
 * - The target size can be updated dynamically.
 *
 * @example
 * ```typescript
 * const policy = new SizePreferencePolicyDefaultImpl({ size: 720, dimension: "height", fallback: "lower" });
 * const selectedFormat = policy.pickFromFormats(formats);
 * ```
 *
 * @see SizePreferencePolicy
 * @see SizePreferenceOptions
 * @see ParsedFormat
 */
class SizePreferencePolicyDefaultImpl implements SizePreferencePolicy {
    
    
    private current: SizePreferenceOptions;


    constructor(startingPreferences: SizePreferenceOptions) {
        this.current = startingPreferences;
    }  

    
    pickFromFormats(formats: ParsedFormat[]): ParsedFormat | null {
        let output: ParsedFormat | null = null;
        if(formats && formats.length > 0) {
            const sorted = formats.filter((e) => e.parsedSize).sort((a,b) => a.parsedSize.getArea() - b.parsedSize.getArea());
            const withDistances: {w: number, h: number, s: number, p: ParsedFormat}[] = sorted.map((a) => {
                return {
                    w: a.parsedSize.width - this.current.size,
                    h: a.parsedSize.height - this.current.size,
                    s: Math.min(a.parsedSize.width, a.parsedSize.height) - this.current.size, 
                    p: a
                }
            });

            const extractor: (el: {w: number, h: number, s: number, p: ParsedFormat}) => number = (t) => {
                if(this.current.dimension == "width") return t.w;
                else if (this.current.dimension == "height") return t.h;
                else return t.s;
            }
           
            let grossOutput = withDistances.find(el => extractor(el) == 0);
            
            if(!grossOutput) {
                if(this.current.fallback == "lowest") {
                    grossOutput = withDistances.shift();
                }
                else if (this.current.fallback == "highest") {
                    grossOutput = withDistances.pop();
                }
                else {
                    const sPositives = withDistances.filter(x => extractor(x) >= 0).sort((a,b) => extractor(a) - extractor(b))
                    const sNegatives = withDistances.filter(x => extractor(x) < 0).sort((a,b) => extractor(b) - extractor(a))

                    const prio = this.current.fallback == "lower"?[sNegatives,sPositives]:[sPositives,sNegatives];
                    grossOutput = prio[0].shift();
                    if(!grossOutput) grossOutput = prio[1].shift();
                }
            }

            output = grossOutput?.p;
        }
        return output;
    }

 

    updateTargetSize(size: number): void {
        this.current.size = size;
    }

    getTargetSize(): number {
        return this.current.size;
    }
    

}



export function createSizePreference(s: SizePreferenceOptions): SizePreferencePolicy {
    return new SizePreferencePolicyDefaultImpl(s);
}